/**
 * Which commit this frontend bundle was built from.
 *
 * The values are spliced in at build time by vite `define`s produced by
 * `scripts/build-stamp.mjs` (see `vite.config.ts`). This module is the only place that reads
 * them, so the "what counts as a real value" rules live in exactly one place.
 *
 * ⚠ `define`s are JSON, so an unavailable commit arrives as the literal `null`. It must NEVER
 * arrive as the string `"undefined"` — that is what an unset `VITE_*` var inlines to, it is
 * truthy, and treating it as a value is the fleet's statetax outage in miniature. The guards
 * below reject it by name anyway, because a rule that is only enforced by convention is not
 * enforced.
 *
 * ⚠ The `typeof` checks are not defensive noise: in a plain `tsc`/vitest context with no vite
 * transform the identifiers are genuinely undeclared, and a bare reference would throw a
 * ReferenceError at import time.
 */

declare const __BUILD_STAMP__: string | undefined;
declare const __BUILD_COMMIT__: string | null | undefined;
declare const __BUILD_COMMIT_TIME__: string | null | undefined;
declare const __BUILD_DIRTY__: boolean | null | undefined;

/** The one token that means "we do not know". Mirrors `UNKNOWN` in scripts/build-stamp.mjs. */
export const UNKNOWN = 'unknown';

const NOT_A_COMMIT = new Set([
  '',
  'undefined',
  'null',
  'unknown',
  'none',
  'head',
  '0000000000000000000000000000000000000000',
]);

const SHA_RE = /^[0-9a-f]{7,40}$/;

/** Coerces anything to a real commit SHA, or null. Rejects the placeholder texts by name. */
export function normalizeCommit(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (NOT_A_COMMIT.has(v)) return null;
  return SHA_RE.test(v) ? v : null;
}

/** Coerces anything to a parseable timestamp, or null. */
export function normalizeTime(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (v === '' || v === UNKNOWN || v === 'undefined' || v === 'null') return null;
  return Number.isNaN(Date.parse(v)) ? null : v;
}

/** Tri-state on purpose: "clean" and "not known" are different facts. */
export function normalizeDirty(value: unknown): boolean | null {
  if (value === true || value === 'yes' || value === 'true' || value === '1') return true;
  if (value === false || value === 'no' || value === 'false' || value === '0') return false;
  return null;
}

export interface BuildInfo {
  /** False when the bundle carries no stamp at all — i.e. it predates this change. */
  stamped: boolean;
  commit: string | null;
  commitShort: string;
  commitTime: string | null;
  dirty: boolean | null;
  /** The raw tagged literal, or null. This is the string that survives minification. */
  raw: string | null;
}

function read(): BuildInfo {
  const raw = typeof __BUILD_STAMP__ === 'string' ? __BUILD_STAMP__ : null;
  const commit = normalizeCommit(typeof __BUILD_COMMIT__ === 'undefined' ? null : __BUILD_COMMIT__);
  const commitTime = normalizeTime(
    typeof __BUILD_COMMIT_TIME__ === 'undefined' ? null : __BUILD_COMMIT_TIME__,
  );
  const dirty = normalizeDirty(typeof __BUILD_DIRTY__ === 'undefined' ? null : __BUILD_DIRTY__);

  return {
    stamped: raw !== null,
    commit,
    commitShort: commit === null ? UNKNOWN : commit.slice(0, 7),
    commitTime,
    dirty,
    raw,
  };
}

/** The stamp of this bundle. Computed once at module load. */
export const buildInfo: BuildInfo = read();

/**
 * Publishes the stamp onto the global object, and returns it.
 *
 * ⚠ THIS EXISTS TO CREATE A SIDE EFFECT, and that is not incidental — it is the whole point.
 * Measured on the first build of this feature: with nothing importing this module, rollup
 * tree-shook it entirely, the bundle hash did not change, and the stamp literal was ABSENT from
 * the shipped artefact. The feature would have looked finished and done nothing. An assignment to
 * the global object is a side effect rollup must preserve, so the literal survives into the bundle
 * where a deploy check can read it.
 *
 * Also makes the answer available to a human in one step: open devtools on the live app and read
 * `window.__PTC_BUILD__`.
 */
export function publishBuildInfo(): BuildInfo {
  (globalThis as unknown as Record<string, unknown>).__PTC_BUILD__ = buildInfo.raw ?? UNKNOWN;
  (globalThis as unknown as Record<string, unknown>).__PTC_BUILD_INFO__ = buildInfo;
  return buildInfo;
}

/** One-line human rendering. Says "unstamped" and "unknown" out loud rather than hiding them. */
export function describeBuild(info: BuildInfo = buildInfo): string {
  if (!info.stamped) return 'unstamped build';
  if (info.commit === null) return 'stamped, but commit unknown (built without git)';
  const bits = [info.commitShort];
  if (info.commitTime) bits.push(info.commitTime);
  if (info.dirty === true) bits.push('dirty tree');
  return bits.join(' · ');
}
